import React, { useState, useEffect, useRef } from "react";
import { CircularProgress, Skeleton } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useNavigate } from "react-router-dom";
import DateConverter from "@heartinz/date_and_time_processor";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useAuth } from "../../../context/AuthProvider";
import Messages from "../../../constants/Messages";

import ViewStockTransfer from "./ViewStockTransfer";
import ReactPaginate from "react-paginate";
import { usePermission } from "../../../context/PermissionContext";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import { AddIcon, RefreshIcon, SearchIcon, ShowIcon } from "../../../constants/Icons";

const ListStockTransfer = ({ isActive }) => {
  const navigate = useNavigate();
  const [isPreLoading, setPreLoading] = useState(true);
  const [isStockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [ProductSuggestions, setProductSuggestions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isStockModalOpen, setStockModalOpen] = useState(false);
  const [isData, setStockViewData] = useState(null);
  const [buttonLoading, setButtonLoading] = useState({
    type: "", // or "view", "edit", etc.
    id: null,
  });
  const containerRef = useRef(null);
  const searchQueryRef = useRef(null);
  const searchInputRef = useRef(null);
  const ProductSuggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);
  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { permission, hasPermission, globelPopupOpen } = usePermission();
  const perPage = 10;
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);

  const handleSearch = async (term) => {
    if (
      hasPermission("STOCKTRANSFER", "STOCKTRANSFER:search") ||
      permission === undefined
    ) {
      setSearchTerm(term);
      setHasSearched(true);

      if (!term.trim()) {
        setProductSuggestions([]);
        await handleList(0, "");
        setCurrentPage(0);
        return;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (!checkNetwork()) return;
          setSuggestionLoading(true);
          controllerRef.current = new AbortController();
          const response = await AxiosInstance.get(
            `module/stocktransfer/search?searchtxt=${term}`,
            { signal: controllerRef.current.signal },
          );
          setProductSuggestions(response.data.results || []);
        } catch (error) {
          if (error.name !== "CanceledError" && error.name !== "AbortError") {
            handleError(error, showAlert);
          }
        } finally {
          setSuggestionLoading(false);
        }
      }, 200);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    // setSelectedProductCode("");
    if (!currentInputValue.trim()) {
      handleList(0, "");
    } else {
      handleList(0, currentInputValue);
    }
    setProductSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectProduct = (product) => {
    const code = product.transferno;
    // setSearchTerm(code);
    // setSelectedProductCode(code);
    setProductSuggestions([]);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleList(0, code);
    setCurrentPage(0);
  };

  const handleList = async (page = 0, term = "") => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stocktransfer?pageno=${
          page + 1
        }&limit=${perPage}&searchtxt=${term}`,
      );
      const results = response.data.results || [];
      setStockData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const handleOutsideClick = (e) => {
    if (
      ProductSuggestionsRef.current &&
      !ProductSuggestionsRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setHasSearched(false);
      setSearchTerm("");
    }
  };
  // const handleSearch = (e) => {
  //   const query = e.target.value.toLowerCase();
  //   setSearchQuery(query);
  //   const filtered = isStockData.filter(
  //     (item) =>
  //       item.transferto.title?.toLowerCase().includes(query) ||
  //       item.transferto.email?.toLowerCase().includes(query) ||
  //       item.transferno?.toLowerCase().includes(query)
  //   );
  //   setFilteredData(filtered);
  // };

  const handleStockItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stocktransfer/${saleId}`,
      );
      const results = response.data.results;
      return results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleLink = () => {
    if (
      hasPermission("STOCKTRANSFER", "STOCKTRANSFER:add") ||
      permission === undefined
    ) {
      navigate("/stocktransfer");
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleView = async (item) => {
    setButtonLoading({ type: "view", id: item._id });
    try {
      const data = await handleStockItems(item._id);
      if (data) {
        setStockViewData(data);
        setStockModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    } finally {
      setButtonLoading({ type: "", id: null });
    }
  };

  const handleClose = () => {
    setStockModalOpen(false);
    setStockViewData(null);
  };

  const handleStockUpdate = ({ updatedStock, transferid }) => {
    if (updatedStock) {
      setStockData((prevData) =>
        prevData.map((item) =>
          item._id === updatedStock._id ? updatedStock : item,
        ),
      );
      setStockData((prevData) =>
        prevData.map((item) =>
          item._id === updatedStock._id ? updatedStock : item,
        ),
      );
      setStockViewData(updatedStock);
    } else {
      setStockData((prevData) =>
        prevData.filter((item) => item._id !== transferid),
      );
      setStockData((prevData) =>
        prevData.filter((item) => item._id !== transferid),
      );
      setStockViewData(null);
    }
  };

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && ProductSuggestions[activeIndex]) {
        e.preventDefault();
        handleSelectProduct(ProductSuggestions[activeIndex]);
      } else {
        e.preventDefault();
        handleSearchFind();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < ProductSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : ProductSuggestions.length - 1,
      );
    }
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    searchTerms: isSearchTerm,
    suggestions: ProductSuggestions,
    modals: {
      isStockModalOpen,
      globelPopupOpen,
    },
    onEnter: handleView,
  });

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const getColorStatus = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-label-warning";
      case "accepted":
        return "bg-label-success";
      default:
        return "bg-label-secondary";
    }
  };

  useEffect(() => {
    handleList(currentPage, isSearchTerm);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleList(currentPage, isSearchTerm);
  };

  useEffect(() => {
    searchQueryRef.current?.focus();
  }, []);

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const getText = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "Pending";
      case "ACCEPTED":
        return "Accepted";
      case "RECEIVED":
        return "Transfered";
      default:
        return status;
    }
  };

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
              <button
                type="button"
                className="btn btn-sm btn-light p-1 shadow-none"
                onClick={handleRefresh}
                disabled={isPreLoading}
                title="Refresh list"
              >
                <RefreshIcon iClass={`${isPreLoading ? "bx-spin" : ""}`}/>
              </button>
              <div className="breadcrumb-title pe-3">List Stock Transfer</div>
              {/* <div className="ms-auto">
                <button
                  type="button"
                  className="btn btn-primary mt-2 mt-lg-0"
                  onClick={handleLink}
                >
                  <i className="ng ng-ng-add-icon"></i> Add Stock Transfer
                </button>
              </div> */}
            </div>

            <div className="row">
              <div className="col">
                <div className="card radius-10 mb-0">
                  <div className="card-body px-0">
                    <div className="row px-3">
                      <div className="col-lg-6 col-12">
                        <div className="ms-auto position-relative">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search transfer by transferno, name, email..."
                              ref={searchInputRef}
                              value={isSearchTerm}
                              onChange={(e) => {
                                handleSearch(e.target.value);
                                setActiveIndex(-1);
                              }}
                              onKeyDown={handleSuggestionNavigation}
                              autoFocus
                            />
                            <button className="input-group-text">
                              <SearchIcon/>
                            </button>
                          </div>
                          {hasSearched &&
                            isSearchTerm &&
                            !suggestionLoading &&
                            ProductSuggestions.length === 0 && (
                              <ul className="suggestions-list">
                                <li className="suggestion-item text-muted">
                                  No products data was found
                                </li>
                              </ul>
                            )}

                          {ProductSuggestions.length > 0 && (
                            <ul className="suggestions-list">
                              {ProductSuggestions.map((product, index) => (
                                <li
                                  key={product._id}
                                  ref={(el) =>
                                    (suggestionRefs.current[index] = el)
                                  }
                                  onClick={() => handleSelectProduct(product)}
                                  className={`suggestion-item ${
                                    index === activeIndex
                                      ? "active-suggestion"
                                      : ""
                                  }`}
                                >
                                  <span className="badge rounded-pill bg-label-navy">
                                    {product.transferno}
                                  </span>{" "}
                                  {/* - {product.totalItems} Items -{" "}
                                  <span className="badge bg-label-fall rounded-pill">
                                    ₹
                                    {CurrencyConverter.toCurrency(
                                      product.grandtotal || 0,
                                      "INR"
                                    )}
                                  </span> */}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6 col-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-teal mt-2 mt-lg-0"
                          onClick={handleLink}
                        >
                          <AddIcon/> New Stock
                          Transfer
                        </button>
                      </div>
                    </div>

                    <div ref={containerRef}>
                      <Table
                        loading={isPreLoading}
                        data={isStockData}
                        onRowClick={(item) => handleView(item)}
                        columns={[
                          {
                            label: "S.No",
                            render: (_, index) => <>{index + 1}</>,
                          },
                          {
                            label: "Transfer No",
                            render: (item) => (
                              <span className="badge rounded-pill bg-label-sun">
                                {item.transferno}
                              </span>
                            ),
                          },
                          {
                            label: "Transfer Date",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate">
                                {item.transferdate
                                  ? DateConverter.ISOToDisplayFormat(
                                      DateConverter.convertToTimeZone(
                                        item.transferdate,
                                        isUser?.timezone,
                                      ),
                                    ).date
                                  : "N/A"}
                              </span>
                            ),
                          },
                          {
                            label: "Transfer To",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate ">
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    item.transferto?.stype,
                                  )}`}
                                >
                                  {item.transferto?.stype}
                                </span>{" "}
                                - {item.transferto.title}
                                <div>
                                  <small>{item.transferto.email}</small>
                                </div>
                              </span>
                            ),
                          },
                          {
                            label: "Transfer Items",
                            render: (item) => <> {item.totalItems} Item(s)</>,
                          },
                          {
                            label: "Amount",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate text-right">
                               
                                {formatCurrencyFun(item.grandtotal)}
                              </span>
                            ),
                          },
                          {
                            label: "Status",
                            render: (item) => (
                              <div className="no-print position-relative">
                              <div className=" d-flex justify-content-center align-items-center">
                              <span
                                className={`stock-status-badge badge rounded-pill ${getColorStatus(
                                  item.status,
                                )}`}
                              >
                                {getText(item.status)}
                              </span>
                              <div className="order-actions order-actions3 opacity-0 ">
                              <button
                                  type="button"
                                  className="btn-label-primary shadow-none me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleView(item);
                                  }}
                                >
                                  <ShowIcon/>
                                </button>
                                </div>
                              </div>
                              </div>
                            ),
                          },
                          // {
                          //   label: "Actions",
                          //   render: (item) => (
                          //     <div className="order-actions opacity-0 opacity-md-100">
                          //       <button
                          //         type="button"
                          //         className="btn-label-info shadow-none me-2"
                          //         onClick={(e) => {
                          //           e.stopPropagation();
                          //           handleView(item);
                          //         }}
                          //         disabled={
                          //           buttonLoading.type === "view" &&
                          //           buttonLoading.id === item._id
                          //         }
                          //       >
                          //         {buttonLoading.type === "view" &&
                          //         buttonLoading.id === item._id ? (
                          //           <CircularProgress
                          //             size={20}
                          //             className="text-primary"
                          //           />
                          //         ) : (
                          //           <i className="bx bx-show"></i>
                          //         )}
                          //       </button>
                          //     </div>
                          //   ),
                          // },
                        ]}
                      />
                    </div>
                    {totalPages > 0 && (
                      <div className="d-flex justify-content-center mt-3">
                        <ReactPaginate
                          previousLabel={"← Previous"}
                          nextLabel={"Next →"}
                          breakLabel={"..."}
                          onPageChange={({ selected }) => {
                            setPreLoading(true);
                            setCurrentPage(selected);
                          }}
                          pageRangeDisplayed={5}
                          pageCount={totalPages}
                          containerClassName="pagination"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isStockModalOpen && (
              <ViewStockTransfer
                data={isData}
                onClose={handleClose}
                onStockUpdate={handleStockUpdate}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ListStockTransfer;
